class ProductAddonList extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute("form")];

    if (!this.form) {
      return;
    }

    this.abortController = new AbortController();
    this.submitting = false;

    this.addEventListener("click", this.onQuantityButtonClick.bind(this), {
      signal: this.abortController.signal
    });
    this.addEventListener("change", this.onQuantityInputChange.bind(this), {
      signal: this.abortController.signal
    });
    this.form.addEventListener("submit", this.onSubmit.bind(this), {
      capture: true,
      signal: this.abortController.signal
    });

    this.querySelectorAll("[data-addon-quantity]").forEach((input) => this.updateQuantityState(input));
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  onQuantityButtonClick(event) {
    const button = event.target.closest("[data-addon-decrease], [data-addon-increase]");

    if (!button || !this.contains(button)) {
      return;
    }

    const input = button.parentElement.querySelector("[data-addon-quantity]");
    const quantity = this.normalizeQuantity(input, input.value);
    const minimum = Number(input.dataset.minimum) || 1;
    const increment = Number(input.dataset.increment) || 1;
    const maximum = this.maximumValidQuantity(input);

    if (button.hasAttribute("data-addon-increase")) {
      const nextQuantity = quantity === 0 ? minimum : quantity + increment;
      input.value = nextQuantity <= maximum ? nextQuantity : quantity;
    } else {
      input.value = quantity <= minimum ? 0 : Math.max(minimum, quantity - increment);
    }

    this.updateQuantityState(input);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  onQuantityInputChange(event) {
    if (!event.target.matches("[data-addon-quantity]")) {
      return;
    }

    event.target.value = this.normalizeQuantity(event.target, event.target.value);
    this.updateQuantityState(event.target);
  }

  normalizeQuantity(input, value) {
    const minimum = Number(input.dataset.minimum) || 1;
    const increment = Number(input.dataset.increment) || 1;
    const maximum = this.maximumValidQuantity(input);
    const parsedValue = Math.max(0, Math.floor(Number(value) || 0));

    if (parsedValue === 0) {
      return 0;
    }

    const normalizedValue = minimum + Math.max(0, Math.floor((parsedValue - minimum) / increment)) * increment;
    return Math.min(normalizedValue, maximum);
  }

  maximumValidQuantity(input) {
    if (input.max === "") {
      return Number.POSITIVE_INFINITY;
    }

    const minimum = Number(input.dataset.minimum) || 1;
    const increment = Number(input.dataset.increment) || 1;
    const maximum = Number(input.max);
    return minimum + Math.max(0, Math.floor((maximum - minimum) / increment)) * increment;
  }

  updateQuantityState(input) {
    const quantity = Number(input.value) || 0;
    const maximum = this.maximumValidQuantity(input);
    const quantitySelector = input.closest(".yg-addon-products__quantity");

    quantitySelector.querySelector("[data-addon-decrease]").disabled = quantity === 0;
    quantitySelector.querySelector("[data-addon-increase]").disabled = quantity >= maximum;
    input.closest("[data-addon-item]").classList.toggle("is-selected", quantity > 0);
  }

  selectedItems() {
    return Array.from(this.querySelectorAll("[data-addon-quantity]"))
      .map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: this.normalizeQuantity(input, input.value)
      }))
      .filter((item) => item.quantity > 0);
  }

  async onSubmit(event) {
    const addonItems = this.selectedItems();

    if (addonItems.length === 0) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.submitting || !this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const submitButtons = Array.from(this.form.elements).filter((element) => element.type === "submit");
    submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));
    this.submitting = true;

    try {
      const sections = ["variant-added"];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections }
      }));

      const formData = new FormData(this.form);
      const mainItem = {
        id: Number(formData.get("id")),
        quantity: Number(formData.get("quantity")) || 1
      };
      const properties = {};

      for (const [name, value] of formData.entries()) {
        const propertyMatch = name.match(/^properties\[(.*)\]$/);

        if (propertyMatch && value !== "") {
          properties[propertyMatch[1]] = value;
        }
      }

      if (Object.keys(properties).length > 0) {
        mainItem.properties = properties;
      }

      if (formData.get("selling_plan")) {
        mainItem.selling_plan = Number(formData.get("selling_plan"));
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items: [mainItem, ...addonItems],
          sections: sections.join(","),
          sections_url: `${Shopify.routes.root}variants/${mainItem.id}`
        })
      });
      const responseJson = await response.json();

      if (!response.ok) {
        this.form.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: responseJson.description || responseJson.message }
        }));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        return;
      }

      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      this.form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: responseJson.items || [responseJson],
          cart: cartContent
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cartContent
        }
      }));
    } catch (error) {
      this.form.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: { error: error.message }
      }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } finally {
      submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
      this.submitting = false;
    }
  }
}

if (!window.customElements.get("product-addon-list")) {
  window.customElements.define("product-addon-list", ProductAddonList);
}

class ProductTermsAcceptance extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute("form")];
    this.checkbox = this.querySelector("[data-terms-checkbox]");
    this.errorElement = this.querySelector("[data-terms-error]");

    if (!this.form || !this.checkbox) {
      return;
    }

    this.abortController = new AbortController();
    this.errorMessage = this.dataset.errorMessage || "Please accept the terms before continuing.";
    this.checkbox.addEventListener("change", this.onChange.bind(this), {
      signal: this.abortController.signal
    });
    this.checkbox.addEventListener("invalid", this.showError.bind(this), {
      signal: this.abortController.signal
    });
    this.form.addEventListener("click", this.onPaymentButtonClick.bind(this), {
      capture: true,
      signal: this.abortController.signal
    });

    this.updateValidity();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.form?.classList.remove("yg-terms-confirmation-required");
  }

  onChange() {
    this.updateValidity();

    if (this.checkbox.checked) {
      this.hideError();
    }
  }

  onPaymentButtonClick(event) {
    if (this.checkbox.checked || !event.target.closest?.(".shopify-payment-button")) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showError();
    this.checkbox.reportValidity();
    this.checkbox.focus({ preventScroll: true });
    this.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  updateValidity() {
    const requiresConfirmation = !this.checkbox.checked;

    this.checkbox.setCustomValidity(requiresConfirmation ? this.errorMessage : "");
    this.form.classList.toggle("yg-terms-confirmation-required", requiresConfirmation);
  }

  showError() {
    if (this.errorElement) {
      this.errorElement.hidden = false;
    }

    this.classList.add("is-invalid");
  }

  hideError() {
    if (this.errorElement) {
      this.errorElement.hidden = true;
    }

    this.classList.remove("is-invalid");
  }
}

if (!window.customElements.get("product-terms-acceptance")) {
  window.customElements.define("product-terms-acceptance", ProductTermsAcceptance);
}
!function(){let e=e=>!function e(t){if(t.length%4!=0||!/^[A-Za-z0-9+/=]+$/.test(t))return!1;try{return atob(t),!0}catch(r){return!1}}(e)?e:decodeURIComponent(escape(window.atob(e))),t={is_mark:"F",shop_domain:window?.Shopify?.shop||"",watermark_text:e("5rC05Y2w5paH5pys"),watermark_size:"20px",watermark_color:"#333333"},r=`${e("aHR0cHM6Ly9haXMueWlndW90ZWNoLmNvbS9zaXRlbWFyay9wcm90ZWN0aW9uL2FwaS92MS93YXRlcm1hcms=")}?shop_domain=${t.shop_domain}`,i=()=>{fetch(r).then(e=>{e.json().then(e=>{"success"===e.result&&(t={...t,...e,expire_time:Date.now()+864e5},"T"===e.is_mark&&n(t),localStorage.setItem("sitemark_watermark",JSON.stringify(t)))})})},s=null,n=e=>{if(s){s.disconnect();return}let{watermark_text:t,watermark_size:r,watermark_color:i}=e,o=document.createElement("div");o.style.position="absolute",o.style.zIndex="9999",o.style.left="50%",o.style.bottom="0",o.style.fontSize=r,o.style.color=i,o.innerHTML=t,setTimeout(()=>{let t=document.querySelector("footer");t?(t.style.position="relative",t.appendChild(o),(s=new MutationObserver(t=>{s.disconnect(),s=null,n(e)})).observe(t,{attributes:!0,childList:!0,subtree:!0})):(o.style.position="fixed",document.body.appendChild(o))},1e3)},o=localStorage.getItem("sitemark_watermark");if(o){let a=JSON.parse(o);t={...t,...a},a.expire_time&&a.expire_time<Date.now()?i():"T"===a.is_mark&&n(t)}else i()}();
